/** Alias keys are lowercase. Canonical product codes are the values. */
export const LANGUAGE_CODE_ALIASES = {
  jp: 'ja',
  zh: 'zh-CN',
  'zh-cn': 'zh-CN',
  pt: 'pt-BR',
  'pt-br': 'pt-BR',
  kr: 'ko',
};

export const LANGUAGE_FLAGS = {
  en: 'flag-gb',
  ja: 'flag-jp',
  ko: 'flag-kr',
  fr: 'flag-fr',
  es: 'flag-es',
  'pt-BR': 'flag-br',
  'pt-PT': 'flag-pt',
  hi: 'flag-in',
  'zh-CN': 'flag-cn',
  id: 'flag-id',
  ru: 'flag-ru',
  ar: 'flag-eg',
  bn: 'flag-bd',
  de: 'flag-de',
  it: 'flag-it',
  tr: 'flag-tr',
  vi: 'flag-vn',
};

export const LANGUAGE_NAMES = {
  en: 'English',
  ja: '日本語',
  ko: '한국어',
  fr: 'Français',
  es: 'Español',
  'pt-BR': 'Português (BR)',
  'pt-PT': 'Português (PT)',
  hi: 'हिंदी',
  'zh-CN': '中文',
  id: 'Bahasa',
  ru: 'Русский',
  ar: 'العربية',
  bn: 'বাংলা',
  de: 'Deutsch',
  it: 'Italiano',
  tr: 'Türkçe',
  vi: 'Tiếng Việt',
};

export const VOICE_LANGUAGE_NAME_TO_CODE = {
  English: 'en',
  Japanese: 'ja',
  Spanish: 'es',
  Hindi: 'hi',
  Portuguese: 'pt-BR',
  'Portuguese (Brazil)': 'pt-BR',
  'Portuguese (Portugal)': 'pt-PT',
  Russian: 'ru',
  French: 'fr',
  Chinese: 'zh-CN',
  Indonesian: 'id',
  Arabic: 'ar',
  Bengali: 'bn',
  German: 'de',
  Italian: 'it',
  Turkish: 'tr',
  Vietnamese: 'vi',
  Korean: 'ko',
};

/**
 * Website UI / translation JSON locales only.
 * Voice TTS may support additional codes; do not use this for voice pickers.
 */
export const SITE_LANGUAGE_CODES = [
  'en', 'fr', 'es', 'ja', 'ko', 'zh-CN', 'pt-BR', 'ru', 'hi', 'ar', 'id',
];

const SITE_LANGUAGE_CODE_SET = new Set(SITE_LANGUAGE_CODES);

/** ElevenLabs v4 voice language pool (TTS only; broader than site UI locales). */
export const ELEVENLABS_VOICE_LANGUAGE_CODES = [
  'en', 'ja', 'ko', 'fr', 'es', 'pt-BR', 'pt-PT', 'hi', 'zh-CN', 'id', 'ru', 'ar', 'bn', 'de', 'it', 'tr', 'vi',
];

/**
 * xAI TTS UI/catalog allowlist (canonical product codes).
 * Regional Arabic/Spanish collapsed to ar / es; zh-CN maps to xAI `zh` at call time.
 */
export const XAI_VOICE_LANGUAGE_CODES = [
  'en', 'ar', 'bn', 'zh-CN', 'fr', 'de', 'hi', 'id', 'it', 'ja', 'ko',
  'pt-BR', 'pt-PT', 'ru', 'es', 'tr', 'vi',
];

/** Union of provider allowlists for client-side fallback validation. */
export const ALL_VOICE_LANGUAGE_CODES = [...new Set([
  ...ELEVENLABS_VOICE_LANGUAGE_CODES,
  ...XAI_VOICE_LANGUAGE_CODES,
])];

/** Our code → xAI BCP-47 `language` param. */
const XAI_TTS_LANGUAGE_MAP = {
  ar: 'ar-SA',
  'zh-CN': 'zh',
  es: 'es-ES',
};

/**
 * Normalize common language-code aliases to canonical product codes.
 * Empty / invalid input → `'en'`.
 * @param {string | null | undefined} languageCode
 * @returns {string}
 */
export const normalizeLanguageCode = (languageCode) => {
  if (!languageCode || typeof languageCode !== 'string') return 'en';
  const trimmed = languageCode.replace(/"/g, '').trim();
  if (!trimmed) return 'en';
  const lower = trimmed.toLowerCase();
  return LANGUAGE_CODE_ALIASES[lower] || trimmed;
};

/**
 * Normalize then clamp to a site UI translation locale.
 * Voice-only codes (e.g. de, vi) fall back to `'en'`.
 * @param {string | null | undefined} languageCode
 * @returns {string}
 */
export const normalizeSiteLanguageCode = (languageCode) => {
  const normalized = normalizeLanguageCode(languageCode);
  return SITE_LANGUAGE_CODE_SET.has(normalized) ? normalized : 'en';
};

/**
 * @param {string | null | undefined} provider
 * @returns {string[]}
 */
export const getVoiceLanguageCodesForProvider = (provider) => (
  String(provider || '').toLowerCase() === 'xai'
    ? XAI_VOICE_LANGUAGE_CODES
    : ELEVENLABS_VOICE_LANGUAGE_CODES
);

/**
 * Map our canonical voice-language code to the xAI TTS API language param.
 * @param {string | null | undefined} code
 * @returns {string}
 */
export const toXaiTtsLanguage = (code) => {
  const normalized = normalizeLanguageCode(code);
  return XAI_TTS_LANGUAGE_MAP[normalized] || normalized;
};

export const getLanguageFlag = (code) => LANGUAGE_FLAGS[code] ?? LANGUAGE_FLAGS.en;

export const getLanguageName = (code) => LANGUAGE_NAMES[code] ?? LANGUAGE_NAMES.en;

export const mapVoiceLanguageNameToCode = (name) => VOICE_LANGUAGE_NAME_TO_CODE[name] ?? 'en';
